import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useGetSalesBreakdownQuery } from '../../redux/slices/apiSlice';
import { ColorRing } from 'react-loader-spinner';
import { BarChart2, LayoutGrid, Search, TrendingUp, TrendingDown, Minus, ChevronDown, Download } from 'lucide-react';
import TablePagination from '@mui/material/TablePagination';

const PAGE_SIZE_OPTIONS = [50, 100, 250];

// ── Formatters ────────────────────────────────────────────────────────────────

function fmtVal(cents) {
  if (cents == null) return '-';
  const usd = cents / 100;
  const abs = Math.abs(usd);
  const sign = usd < 0 ? '-' : '';
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(1)}K`;
  return `${sign}$${abs.toFixed(0)}`;
}

function fmtCount(n) {
  if (n == null) return '-';
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 1_000_000) return `${sign}${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}${(abs / 1_000).toFixed(1)}K`;
  return String(n);
}

function fmtPct(pct) {
  if (pct == null) return '-';
  return `${pct > 0 ? '+' : ''}${pct.toFixed(1)}%`;
}

// ── Delta renderers ───────────────────────────────────────────────────────────

// pct can arrive as a plain number or as { source, parsedValue } from the backend
function normalizePct(pct) {
  if (pct == null) return null;
  if (typeof pct === 'object') return pct.parsedValue ?? null;
  return pct;
}

// Delta renderer: icon + delta + pct (used in both Combined and Single views)
function DeltaWithPct({ delta, pct, isValue = false }) {
  if (delta == null) return <span className="text-gray-400 text-xs">—</span>;
  const positive = delta > 0;
  const zero = delta === 0;
  const color = zero ? 'text-gray-500' : positive ? 'text-green-600' : 'text-red-500';
  const Icon = zero ? Minus : positive ? TrendingUp : TrendingDown;
  const formattedDelta = isValue
    ? (positive ? `+${fmtVal(delta)}` : fmtVal(delta))
    : (positive ? `+${delta}` : String(delta));
  const pctNum = normalizePct(pct);
  const formattedPct = pctNum != null ? ` (${Math.abs(pctNum).toFixed(1)}%)` : '';
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${color} whitespace-nowrap`}>
      <Icon className="w-3 h-3" />{formattedDelta}{formattedPct}
    </span>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

const COLOR_GRADE_STYLE = {
  D: 'text-blue-700 font-semibold',
  E: 'text-blue-600 font-semibold',
  F: 'text-green-600 font-semibold',
  G: 'text-green-500',
  H: 'text-yellow-600',
  I: 'text-orange-500',
  J: 'text-red-500',
  K: 'text-red-600',
};

function SummaryCard({ title, current, prior, priYear, deltaPct }) {
  const isPositive = deltaPct != null && deltaPct > 0;
  const isNegative = deltaPct != null && deltaPct < 0;
  const pctColor = isPositive ? 'text-green-600' : isNegative ? 'text-red-500' : 'text-gray-500';
  const arrow = isPositive ? '↑' : isNegative ? '↓' : '';

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <p className="text-xs text-gray-500 font-medium">{title}</p>
      <div className="mt-1 flex items-baseline gap-2 flex-wrap">
        <span className="text-xl font-bold text-gray-900">{current}</span>
        {deltaPct != null && (
          <span className={`text-xs font-semibold ${pctColor}`}>
            {arrow} {Math.abs(deltaPct).toFixed(1)}%
          </span>
        )}
      </div>
      {prior != null && priYear && (
        <p className="text-xs text-gray-400 mt-0.5">{priYear}: {prior}</p>
      )}
    </div>
  );
}

function FilterSelect({ value, onChange, options }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs text-gray-700 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400 cursor-pointer"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

function MultiSelectDropdown({ label, value, onChange, options }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = (val) => {
    onChange(value.includes(val) ? value.filter((v) => v !== val) : [...value, val]);
  };

  const displayLabel = value.length === 0
    ? `All ${label}`
    : value.length === 1
      ? (options.find((o) => o.value === value[0])?.label || value[0])
      : `${label} (${value.length})`;

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs text-gray-700 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400 cursor-pointer flex items-center gap-1 whitespace-nowrap"
      >
        {displayLabel}
        <ChevronDown className="w-3 h-3 text-gray-400" />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-lg min-w-[150px] max-h-52 overflow-y-auto">
          {value.length > 0 && (
            <button
              onClick={() => onChange([])}
              className="w-full text-left px-3 py-1.5 text-xs text-blue-600 hover:bg-gray-50 border-b border-gray-100"
            >
              Clear all
            </button>
          )}
          {options.map((o) => (
            <label key={o.value} className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 cursor-pointer text-xs text-gray-700">
              <input
                type="checkbox"
                checked={value.includes(o.value)}
                onChange={() => toggle(o.value)}
                className="w-3 h-3 accent-blue-600"
              />
              {o.label}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

const GROUP_BY_TABS = [
  { value: 'shape',       label: 'Shape' },
  { value: 'carat_range', label: 'Carat Range' },
  { value: 'color',       label: 'Color' },
  { value: 'clarity',     label: 'Clarity' },
];

// ── Sort helper ───────────────────────────────────────────────────────────────

const TH_BASE = 'px-3 py-2 text-left font-semibold text-gray-500 whitespace-nowrap border-b border-gray-200 bg-gray-50';

function SortableTh({ label, sortKey, activeSortBy, ascDesc, onSort, right }) {
  const isActive = activeSortBy === sortKey;
  return (
    <th
      onClick={() => onSort(sortKey)}
      className={`${TH_BASE} cursor-pointer select-none hover:text-blue-600 ${right ? 'text-right' : 'text-left'}`}
    >
      {label}
      {isActive && <span className="ml-1 text-blue-500">{ascDesc === 'asc' ? '▲' : '▼'}</span>}
    </th>
  );
}

function StaticTh({ label, right }) {
  return <th className={`${TH_BASE} ${right ? 'text-right' : 'text-left'}`}>{label}</th>;
}

// ── Tables ────────────────────────────────────────────────────────────────────

// "Combined" UI (view=single): 4-column dimension breakdown with pagination
function CombinedTable({ rows, curYear, priYear, page, pageSize, onPageChange, onPageSizeChange, sortBy, ascDesc, onSort }) {
  if (!rows.length) return <EmptyState />;

  const start = page * pageSize;
  const pageRows = rows.slice(start, start + pageSize);

  return (
    <div className="flex flex-col">
      <div className="overflow-x-auto max-h-[60vh] overflow-y-auto rounded-lg border border-gray-100">
        <table className="w-full border-collapse text-xs">
          <thead className="sticky top-0 bg-gray-50 z-10">
            <tr>
              <StaticTh label="Shape" />
              <StaticTh label="Carat" />
              <StaticTh label="Color" />
              <StaticTh label="Clarity" />
              <SortableTh right label={`${curYear} Cnt`} sortKey="current_count"   activeSortBy={sortBy} ascDesc={ascDesc} onSort={onSort} />
              <SortableTh right label={`${curYear} Val`} sortKey="current_value"   activeSortBy={sortBy} ascDesc={ascDesc} onSort={onSort} />
              <StaticTh right label={`${priYear} Cnt`} />
              <StaticTh right label={`${priYear} Val`} />
              <SortableTh right label="Count Change" sortKey="count_change_pct" activeSortBy={sortBy} ascDesc={ascDesc} onSort={onSort} />
              <SortableTh right label="Value Change" sortKey="value_change_pct" activeSortBy={sortBy} ascDesc={ascDesc} onSort={onSort} />
              <StaticTh right label="Inv" />
            </tr>
          </thead>
          <tbody>
            {pageRows.map((row, i) => (
              <tr key={i} className="border-b border-gray-50 hover:bg-blue-50 transition-colors">
                <td className="px-3 py-2 font-medium text-gray-800 whitespace-nowrap">
                  {row.translated_shape || row.shape}
                </td>
                <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{row.carat_range}</td>
                <td className={`px-3 py-2 whitespace-nowrap ${COLOR_GRADE_STYLE[row.translated_color] || 'text-gray-600'}`}>
                  {row.translated_color || row.color}
                </td>
                <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                  {row.translated_clarity || row.clarity}
                </td>
                <td className="px-3 py-2 text-right font-medium text-gray-800">{row.current_count}</td>
                <td className="px-3 py-2 text-right font-medium text-gray-800">{fmtVal(row.current_value_cents)}</td>
                <td className="px-3 py-2 text-right text-gray-400">{row.prior_count}</td>
                <td className="px-3 py-2 text-right text-gray-400">{fmtVal(row.prior_value_cents)}</td>
                <td className="px-3 py-2 text-right">
                  <DeltaWithPct delta={row.count_delta} pct={normalizePct(row.count_delta_pct)} />
                </td>
                <td className="px-3 py-2 text-right">
                  <DeltaWithPct delta={row.value_delta_cents} pct={normalizePct(row.value_delta_pct)} isValue />
                </td>
                <td className="px-3 py-2 text-right text-gray-500">{row.inventory_count ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="border border-t-0 border-gray-100 rounded-b-lg bg-white">
        <TablePagination
          component="div"
          count={rows.length}
          page={page}
          onPageChange={(_, newPage) => onPageChange(newPage)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => { onPageSizeChange(parseInt(e.target.value, 10)); onPageChange(0); }}
          rowsPerPageOptions={PAGE_SIZE_OPTIONS}
        />
      </div>
    </div>
  );
}

// "Single" UI (view=combined): single dimension column + delta with %
function SingleTable({ rows, curYear, priYear, groupBy, sortBy, ascDesc, onSort }) {
  if (!rows.length) return <EmptyState />;
  const dimLabel = GROUP_BY_TABS.find((t) => t.value === groupBy)?.label || 'Dimension';
  return (
    <div className="overflow-x-auto max-h-[60vh] overflow-y-auto rounded-lg border border-gray-100">
      <table className="w-full border-collapse text-xs">
        <thead className="sticky top-0 bg-gray-50 z-10">
          <tr>
            <StaticTh label={dimLabel} />
            <SortableTh right label={`${curYear} Count`} sortKey="current_count"   activeSortBy={sortBy} ascDesc={ascDesc} onSort={onSort} />
            <SortableTh right label={`${curYear} Value`} sortKey="current_value"   activeSortBy={sortBy} ascDesc={ascDesc} onSort={onSort} />
            <StaticTh right label={`${priYear} Count`} />
            <StaticTh right label={`${priYear} Value`} />
            <SortableTh right label="Count Change" sortKey="count_change_pct" activeSortBy={sortBy} ascDesc={ascDesc} onSort={onSort} />
            <SortableTh right label="Value Change" sortKey="value_change_pct" activeSortBy={sortBy} ascDesc={ascDesc} onSort={onSort} />
            <StaticTh right label="Inventory" />
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-gray-50 hover:bg-blue-50 transition-colors">
              <td className="px-3 py-2 font-medium text-gray-800 whitespace-nowrap">
                {row.translated_dimension || row.dimension}
              </td>
              <td className="px-3 py-2 text-right font-medium text-gray-800">{fmtCount(row.current_count)}</td>
              <td className="px-3 py-2 text-right font-medium text-gray-800">{fmtVal(row.current_value_cents)}</td>
              <td className="px-3 py-2 text-right text-gray-400">{fmtCount(row.prior_count)}</td>
              <td className="px-3 py-2 text-right text-gray-400">{fmtVal(row.prior_value_cents)}</td>
              <td className="px-3 py-2 text-right">
                <DeltaWithPct delta={row.count_delta} pct={row.count_delta_pct} />
              </td>
              <td className="px-3 py-2 text-right">
                <DeltaWithPct delta={row.value_delta_cents} pct={row.value_delta_pct} isValue />
              </td>
              <td className="px-3 py-2 text-right text-gray-500 whitespace-nowrap">
                {row.inventory_count != null ? `${row.inventory_count} / ${fmtVal(row.inventory_value_cents)}` : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EmptyState() {
  return <div className="text-center py-12 text-sm text-gray-400">No data available</div>;
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function YoYSalesAnalysis({ accountDetails }) {
  const accountId = accountDetails?.account_id;

  // 'combined' = all 4 dims per row (API view=single)
  // 'single'   = grouped by one dim (API view=combined)
  const [uiView, setUiView] = useState('combined');
  const [groupBy, setGroupBy] = useState('shape');

  // Dimension filters (multi-select)
  const [shapes, setShapes] = useState([]);
  const [caratRanges, setCaratRanges] = useState([]);
  const [colors, setColors] = useState([]);
  const [clarities, setClarities] = useState([]);

  // Controls
  const [sortBy, setSortBy] = useState('value_change_pct');
  const [ascDesc, setAscDesc] = useState('desc');
  const [directionFilter, setDirectionFilter] = useState('all');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  // Combined-view pagination
  const [combinedPage, setCombinedPage] = useState(0);
  const [combinedPageSize, setCombinedPageSize] = useState(50);

  const apiView = uiView === 'combined' ? 'single' : 'combined';

  const handleSort = (column) => {
    if (column === sortBy) {
      setAscDesc((prev) => (prev === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortBy(column);
      setAscDesc('desc');
    }
  };

  const queryArgs = useMemo(() => {
    const args = {
      seller_account_id: accountId,
      view: apiView,
      sort_by: sortBy,
      asc_desc: ascDesc,
      direction_filter: directionFilter,
    };
    if (uiView === 'single') args.group_by = groupBy;
    if (shapes.length) args.shape = shapes;
    if (caratRanges.length) args.carat_range = caratRanges;
    if (colors.length) args.color = colors;
    if (clarities.length) args.clarity = clarities;
    if (search) args.search = search;
    return args;
  }, [accountId, apiView, uiView, groupBy, shapes, caratRanges, colors, clarities, sortBy, ascDesc, directionFilter, search]);

  // Reset to page 0 whenever the query changes
  useEffect(() => { setCombinedPage(0); }, [queryArgs]);

  const { data, isLoading, isFetching } = useGetSalesBreakdownQuery(queryArgs, {
    skip: !accountId,
  });

  const summary = data?.summary;
  const rows = data?.rows || [];
  const opts = data?.available_options || {};
  const curYear = data?.current_year;
  const priYear = data?.comparison_year;
  const dirCounts = summary?.direction_counts || {};
  const loading = isLoading || isFetching;

  const downloadCSV = () => {
    if (!rows.length) return;
    const toVal = (cents) => cents != null ? (cents / 100).toFixed(2) : '';
    const toPct = (p) => { const n = normalizePct(p); return n != null ? n.toFixed(1) : ''; };

    let headers, getRow;
    if (uiView === 'combined') {
      headers = ['Shape', 'Carat', 'Color', 'Clarity',
        `${curYear} Count`, `${curYear} Value (USD)`,
        `${priYear} Count`, `${priYear} Value (USD)`,
        'Count Change', 'Count Change %', 'Value Change (USD)', 'Value Change %',
        'Inventory Count'];
      getRow = (r) => [
        r.translated_shape || r.shape, r.carat_range,
        r.translated_color || r.color, r.translated_clarity || r.clarity,
        r.current_count, toVal(r.current_value_cents),
        r.prior_count, toVal(r.prior_value_cents),
        r.count_delta, toPct(r.count_delta_pct),
        toVal(r.value_delta_cents), toPct(r.value_delta_pct),
        r.inventory_count ?? '',
      ];
    } else {
      const dimLabel = GROUP_BY_TABS.find((t) => t.value === groupBy)?.label || 'Dimension';
      headers = [dimLabel,
        `${curYear} Count`, `${curYear} Value (USD)`,
        `${priYear} Count`, `${priYear} Value (USD)`,
        'Count Change', 'Count Change %', 'Value Change (USD)', 'Value Change %',
        'Inventory Count', 'Inventory Value (USD)'];
      getRow = (r) => [
        r.translated_dimension || r.dimension,
        r.current_count, toVal(r.current_value_cents),
        r.prior_count, toVal(r.prior_value_cents),
        r.count_delta, toPct(r.count_delta_pct),
        toVal(r.value_delta_cents), toPct(r.value_delta_pct),
        r.inventory_count ?? '', toVal(r.inventory_value_cents),
      ];
    }

    const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const csv = [headers, ...rows.map(getRow)].map((row) => row.map(escape).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sales_yoy_${uiView}_${curYear ?? 'data'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="px-6 py-6 space-y-6">
      {/* Title */}
      <div>
        <h2 className="text-xl font-bold text-gray-900">Year-over-Year Sales Analysis</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          Compare {curYear || '—'} sales performance with {priYear || '—'} across all dimensions
        </p>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-4 gap-4">
          <SummaryCard
            title="Total Sales Count"
            current={fmtCount(summary.total_sales_count?.current)}
            prior={fmtCount(summary.total_sales_count?.prior)}
            priYear={priYear}
            deltaPct={summary.total_sales_count?.delta_pct}
          />
          <SummaryCard
            title="Total Sales Value"
            current={fmtVal(summary.total_sales_value?.current_cents)}
            prior={fmtVal(summary.total_sales_value?.prior_cents)}
            priYear={priYear}
            deltaPct={summary.total_sales_value?.delta_pct}
          />
          <SummaryCard
            title="Avg Price per Stone"
            current={fmtVal(summary.avg_price_per_stone?.current_cents)}
            prior={fmtVal(summary.avg_price_per_stone?.prior_cents)}
            priYear={priYear}
            deltaPct={summary.avg_price_per_stone?.delta_pct}
          />
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <p className="text-xs text-gray-500 font-medium">YoY Performance</p>
            <div className="mt-3 space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-600">Count:</span>
                <span className={`text-sm font-bold ${(summary.yoy_performance?.count_change_pct ?? 0) >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                  {fmtPct(summary.yoy_performance?.count_change_pct)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-600">Value:</span>
                <span className={`text-sm font-bold ${(summary.yoy_performance?.value_change_pct ?? 0) >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                  {fmtPct(summary.yoy_performance?.value_change_pct)}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sales Breakdown card */}
      <div className="bg-white border border-gray-200 rounded-xl p-4">

        {/* Header: title + Single/Combined + group-by tabs */}
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <BarChart2 className="h-4 w-4 text-blue-600" />
            <span className="text-sm font-semibold text-gray-800">Sales Breakdown</span>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {uiView === 'single' && (
              <div className="flex border border-gray-200 rounded-lg overflow-hidden text-xs font-medium">
                {GROUP_BY_TABS.map((tab, idx) => (
                  <div
                    key={tab.value}
                    onClick={() => setGroupBy(tab.value)}
                    className={`px-3 py-1.5 transition-colors cursor-pointer whitespace-nowrap ${
                      idx > 0 ? 'border-l border-gray-200' : ''
                    } ${
                      groupBy === tab.value
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {tab.label}
                  </div>
                ))}
              </div>
            )}
            {/* Single / Combined toggle */}
            <div className="flex border border-gray-200 rounded-lg overflow-hidden text-xs font-medium">
              <div
                onClick={() => setUiView('single')}
                className={`flex items-center cursor-pointer gap-1.5 px-3 py-1.5 transition-colors ${
                  uiView === 'single' ? 'bg-gray-800 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                <BarChart2 className="h-3.5 w-3.5" /> Single
              </div>
              <div
                onClick={() => setUiView('combined')}
                className={`flex items-center cursor-pointer gap-1.5 px-3 py-1.5 border-l border-gray-200 transition-colors ${
                  uiView === 'combined' ? 'bg-gray-800 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                <LayoutGrid className="h-3.5 w-3.5" /> Combined
              </div>
            </div>

            {/* Group-by tabs — only visible in Single mode */}
            
          </div>
        </div>

        {/* Controls: direction + sort + search */}
        <div className="flex items-center gap-3 mb-3 flex-wrap">
          <FilterSelect
            value={directionFilter}
            onChange={setDirectionFilter}
            options={[
              { value: 'all',  label: `All (${dirCounts.total ?? '—'})` },
              { value: 'up',   label: `Up (${dirCounts.up ?? '—'})` },
              { value: 'down', label: `Down (${dirCounts.down ?? '—'})` },
            ]}
          />
          <div className="relative flex-1 min-w-[180px] max-w-xs">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') setSearch(searchInput); }}
              onBlur={() => setSearch(searchInput)}
              placeholder="Search dimensions..."
              className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-400"
            />
          
          </div>
            {/* Dimension filters + row count */}
        <div className="flex items-center gap-2 flex-wrap">
          <MultiSelectDropdown label="Shapes"    value={shapes}      onChange={setShapes}      options={opts.shapes       || []} />
          <MultiSelectDropdown label="Sizes"     value={caratRanges} onChange={setCaratRanges} options={opts.carat_ranges || []} />
          <MultiSelectDropdown label="Colors"    value={colors}      onChange={setColors}      options={opts.colors       || []} />
          <MultiSelectDropdown label="Clarities" value={clarities}   onChange={setClarities}   options={opts.clarities    || []} />
        </div>
        <div className="ml-auto flex items-center gap-2 whitespace-nowrap">
          <span className="text-xs text-gray-400">
            Showing {rows.length} of {data?.count ?? '—'} combinations
          </span>
          <div
            onClick={downloadCSV}
            disabled={!rows.length}
      
            className="flex items-center gap-1 px-2 py-1.5 text-xs   cursor-pointer border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <Download className="w-3 h-3" /> Download
          </div>
        </div>
        </div>

        

        {/* Table */}
        {loading ? (
          <div className="flex justify-center items-center h-32">
            <ColorRing visible height="50" width="50" ariaLabel="loading"
              colors={['#00319f', '#2e56b1', '#5c7bc2', '#8aa1d3', '#b2c1e2']} />
          </div>
        ) : uiView === 'single' ? (
          <SingleTable rows={rows} curYear={curYear} priYear={priYear} groupBy={groupBy} sortBy={sortBy} ascDesc={ascDesc} onSort={handleSort} />
        ) : (
          <CombinedTable
            rows={rows}
            curYear={curYear}
            priYear={priYear}
            page={combinedPage}
            pageSize={combinedPageSize}
            onPageChange={setCombinedPage}
            onPageSizeChange={setCombinedPageSize}
            sortBy={sortBy}
            ascDesc={ascDesc}
            onSort={handleSort}
          />
        )}
      </div>
    </div>
  );
}
